export class APIFilterOption<T> {
  constructor(
    public key: keyof T | string,
    public value: string | number | string[] | number[] | object,
    public exact = false
  ) {}
}

export class APISortOptions<T> {
  //active: (keyof T & string); // TODO: interoperability with mat-angular Sort class would be great, also for type checking.
  active: string = "";
  direction: "asc" | "desc" = "asc";
}

export abstract class BaseRestAPIService {
  // baseUrl is picked up from the build environment when available.
  // For Vite-based builds the variable is `import.meta.env.VITE_API_BASE_URL`.
  // If not provided we fall back to the staging URL used previously.
  protected baseUrl: string;
  public userId: string | undefined;
  public devOnly = true; // FIX: this should be set based on environment, not hardcoded.

  protected constructor(
    protected apiUrl: string
    // protected mapper: (input: any) => ReadDTO,
    // protected securityService: SecurityService,
    // @Optional() protected ability?: PureAbility
  ) {
    // use localhost or local ip for iOS sims, use 10.0.2.2 for Android emulators.
    // if (platform.OS === 'android' && !Device.isDevice) {
    //     this.baseUrl = `http://10.0.2.2:8001/v1`;
    // }

    // Resolve baseUrl from environment (Vite: import.meta.env.VITE_API_BASE_URL)
    // Use a fallback to the previous staging URL. Strip any trailing slashes
    // to avoid duplicate slashes when concatenating endpoint paths.
    const envBase = (
      import.meta as unknown as { env?: { VITE_API_BASE_URL?: string } }
    ).env?.VITE_API_BASE_URL;

    const resolvedBase = envBase ?? "https://api.staging.insifr.vorteq.dev/v1";
    this.baseUrl = resolvedBase.replace(/\/+$/g, "");

    this.apiUrl = `${this.baseUrl}/${this.apiUrl}`;
    console.log(`API Service initialized for ${this.apiUrl}`);
  }

  protected createQueryParams(
    filter: APIFilterOption<any>[] = [],
    pageIndex?: number,
    pageSize?: number
  ): URLSearchParams {
    const queryParams: any = filter.reduce(
      (a: any, filter) => ({
        ...a,
        [filter.key]: a[filter.key]
          ? [a[filter.key], filter.value].join()
          : filter.value,
      }),
      {
        page: pageIndex,
        limit: pageSize,
      }
    );
    return new URLSearchParams(queryParams);
  }

  async get(id: string): Promise<Response | undefined> {
    try {
      const response = await fetch(`${this.apiUrl}/${id}`, {
        headers: {
          // ...await this.getHttpRequestOptions(),
        },
      });
      return await response.json();
    } catch (error) {
      console.error(error);
    }
  }

  async getAll(
    pageIndex = 1,
    pageSize = 10,
    search: string | null = null,
    filter: APIFilterOption<any>[] = [],
    sort: APISortOptions<{ active: string; direction: string }> = {
      active: "id",
      direction: "asc",
    }
  ): Promise<
    | {
        items: any[];
        pagination: any;
      }
    | undefined
  > {
    const filterOptions = this.filtersToUrl(filter);

    if (search && search !== "") {
      filterOptions["search"] = search;
    }

    const sortParam: { [key: string]: string } = {};
    if (sort) {
      sortParam[sort.active] = sort.direction;
    }

    const url = `${this.apiUrl}?page=${pageIndex}&limit=${pageSize}&filter=${JSON.stringify(filterOptions)}&sort=${JSON.stringify(sortParam)}`;
    try {
      const response = await fetch(url, {
        headers: {
          ...(await this.getHttpRequestOptions()),
        },
      });
      const json = await response.json();
      return {
        items: json.items,
        pagination: json.meta,
      };
    } catch (error) {
      console.error(error);
    }
  }

  async create(dto: any): Promise<Response | undefined> {
    try {
      const response = await fetch(this.apiUrl, {
        method: "POST",
        headers: {
          ...(await this.getHttpRequestOptions()),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(dto),
      });
      return await response.json();
    } catch (error) {
      console.error(error);
    }
  }

  async update(id: string, dto: any): Promise<Response | undefined> {
    try {
      const response = await fetch(`${this.apiUrl}/${id}`, {
        method: "PATCH",
        headers: {
          ...(await this.getHttpRequestOptions()),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(dto),
      });
      return await response.json();
    } catch (error) {
      console.error(error);
    }
  }

  async delete(id: string): Promise<Response | undefined> {
    try {
      const response = await fetch(`${this.apiUrl}/${id}`, {
        method: "DELETE",
        headers: {
          ...(await this.getHttpRequestOptions()),
          "Content-Type": "application/json",
        },
      });
      return await response.json();
    } catch (error) {
      console.error(error);
    }
  }

  async put(id: string, dto: any): Promise<Response | undefined> {
    try {
      const response = await fetch(`${this.apiUrl}/${id}`, {
        method: "PUT",
        headers: {
          ...(await this.getHttpRequestOptions()),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(dto),
      });
      return await response.json();
    } catch (error) {
      console.error(error);
    }
  }

  //
  // // TODO: move to util folder?
  // https://stackoverflow.com/a/26909377
  static strToObj(strArr: string[], val: any, startIdx: number = 0) {
    let i,
      obj = {},
      x: { [key: string]: any } = obj;
    for (i = startIdx; i < strArr.length - 1; i++) {
      x = x[strArr[i]] = {};
    }

    x[strArr[i]] = val;
    return obj;
  }

  protected filtersToUrl(filter: APIFilterOption<any>[] = []): {
    [key: string]: any;
  } {
    const filterOptions: { [key: string]: any } = {};
    for (const item of filter) {
      if (
        (item.value !== null && item.value !== "") ||
        (item.value !== null &&
          Array.isArray(item.value) &&
          item.value.length > 0)
      ) {
        let str = item.value;
        if (item.exact) {
          str = "!" + item.value;
        }

        if (!Array.isArray(item.value)) {
          const split = (item.key as string).split(".");

          if (!item.value || split?.length === 1) {
            filterOptions[item.key as string] = str;
          } else {
            const split = (item.key as string).split(".");
            // build an object out of a string notation (for example: a.b.c: "value").
            filterOptions[split[0]] = BaseRestAPIService.strToObj(
              split,
              item.value,
              1
            );
          }
        } else {
          filterOptions[item.key as string] = str;
        }
      }
    }

    return filterOptions;
  }

  async getHttpRequestOptions(user?: any): Promise<any> {
    if (this.devOnly) {
      this.userId = "10000000-0000-0000-0000-000000000000";
      if (user && user.sub) {
        return {
          "debug-auth-id": user.sub,
        };
      }

      if (this.userId) {
        return {
          "debug-user-id": this.userId,
        };
      } else {
        return {};
      }
    }
  }

  parseFormData(doc: any): FormData {
    const formData = new FormData();

    const keys = Object.keys(doc).filter((key) => key !== "file");
    formData.append("file", doc.file, doc.file.name);

    keys.forEach((key) => {
      if (doc[key]) {
        formData.append(key, doc[key]);
      }
    });
    return formData;
  }
}
